/* Views, search, scope and column control — everything that decides which rows the ledger shows.
 *
 * One row of controls above one table, in the order a reader reaches for them: the named view
 * first, then free text, then scope, then what the table shows. Filters that live in a side panel
 * are filters a reader forgets are applied, which is how somebody reports an estate as clean when
 * they are looking at a seventh of it — so the active view is always visible and `Reset` appears
 * the moment anything is narrowing the list.
 *
 * The two read controls on the right are the honest half of this screen. Movement and per-dimension
 * figures are one request per dataset each, so they say so, and they refuse rather than fan out
 * past a bound. Nothing here hides a cost.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';

import { OPTIONAL_COLUMNS } from '../model/types';
import type { OptionalColumn } from '../model/types';
import type { SavedView, TableQuery } from '../model/rows';

export interface DatasetToolbarProps {
  readonly views: readonly SavedView[];
  readonly counts: ReadonlyMap<string, number>;
  readonly query: TableQuery;
  readonly onQueryChange: (next: TableQuery) => void;
  readonly schemas: readonly string[];
  readonly columns: ReadonlySet<OptionalColumn>;
  readonly onToggleColumn: (column: OptionalColumn) => void;
  readonly filtered: boolean;
  readonly onReset: () => void;
  readonly shown: number;
  readonly total: number;
  /** Movement reads: on, how many are in flight, and whether the visible set is small enough. */
  readonly movementOn: boolean;
  readonly movementLoading: boolean;
  readonly onToggleMovement: () => void;
  readonly dimensionsOn: boolean;
  readonly dimensionsLoading: boolean;
  readonly dimensionsAllowed: boolean;
  readonly dimensionLimit: number;
  readonly onToggleDimensions: () => void;
  readonly onSaveView: (label: string) => void;
  readonly onDeleteView: (id: string) => void;
}

/** A disclosure that closes on Escape, on a click outside, and when focus leaves it.
 *
 * Built here rather than reached for from the kit because the kit has no popover: it has a modal, a
 * confirm and a drawer, and every one of those is too heavy for a list of checkboxes. Keeping it
 * local is cheaper than adding a shared component with one consumer.
 */
function Popover({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactElement;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onDown = (event: MouseEvent): void => {
      if (
        box.current !== null &&
        event.target instanceof Node &&
        !box.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <div className="lq-pop" ref={box}>
      <button
        type="button"
        className="lq-btn lq-btn--secondary lq-btn--compact"
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was);
        }}
      >
        {label}
      </button>
      {open && <div className="lq-pop__panel">{children}</div>}
    </div>
  );
}

export function DatasetToolbar(props: DatasetToolbarProps): ReactElement {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [draftLabel, setDraftLabel] = useState('');

  const {
    views,
    counts,
    query,
    onQueryChange,
    schemas,
    columns,
    onToggleColumn,
    filtered,
    onReset,
    shown,
    total,
    movementOn,
    movementLoading,
    onToggleMovement,
    dimensionsOn,
    dimensionsLoading,
    dimensionsAllowed,
    dimensionLimit,
    onToggleDimensions,
    onSaveView,
    onDeleteView,
  } = props;

  const options = views.map((view) => ({
    id: view.id,
    label: view.labelKey === undefined ? (view.label ?? view.id) : t(view.labelKey),
    count: counts.get(view.id),
  }));

  return (
    <div className="lq-dtoolbar">
      <SegmentedFilter
        variant="ledger"
        label={t('scoring.toolbar.viewsLabel')}
        options={options}
        value={query.view}
        onChange={(view) => {
          onQueryChange({ ...query, view });
        }}
      />

      <div className="lq-dtoolbar__search">
        <label className="lq-visually-hidden" htmlFor="scores-search">
          {t('scoring.toolbar.searchLabel')}
        </label>
        <input
          id="scores-search"
          type="search"
          className="lq-input"
          placeholder={t('scoring.toolbar.searchPlaceholder')}
          value={query.search}
          onChange={(event) => {
            onQueryChange({ ...query, search: event.target.value });
          }}
        />
      </div>

      <label className="lq-visually-hidden" htmlFor="scores-schema">
        {t('scoring.toolbar.schemaLabel')}
      </label>
      <select
        id="scores-schema"
        className="lq-input"
        value={query.schema}
        onChange={(event) => {
          onQueryChange({ ...query, schema: event.target.value });
        }}
      >
        <option value="">{t('scoring.toolbar.allSchemas')}</option>
        {schemas.map((schema) => (
          <option key={schema} value={schema}>
            {schema}
          </option>
        ))}
      </select>

      {filtered && (
        <button
          type="button"
          className="lq-btn lq-btn--secondary lq-btn--compact"
          onClick={onReset}
        >
          {t('scoring.toolbar.reset')}
        </button>
      )}

      <span className="lq-dtoolbar__spacer" />

      <span className="lq-dtoolbar__count" role="status">
        {t('scoring.toolbar.showing', { shown, total })}
      </span>

      <Popover label={t('scoring.toolbar.reads')}>
        <div className="lq-pop__group">
          <p className="lq-pop__head">{t('scoring.toolbar.readsHead')}</p>
          <p className="lq-pop__note">{t('scoring.toolbar.readsNote')}</p>
          <label className="lq-pop__check">
            <input type="checkbox" checked={movementOn} onChange={onToggleMovement} />
            <span>
              {t('scoring.toolbar.readMovement')}
              <span className="lq-pop__sub">
                {movementLoading
                  ? t('scoring.toolbar.reading')
                  : t('scoring.toolbar.perDataset', { count: shown })}
              </span>
            </span>
          </label>
          <label className="lq-pop__check">
            <input
              type="checkbox"
              checked={dimensionsOn}
              disabled={!dimensionsAllowed && !dimensionsOn}
              onChange={onToggleDimensions}
            />
            <span>
              {t('scoring.toolbar.readDimensions')}
              <span className="lq-pop__sub">
                {!dimensionsAllowed && !dimensionsOn
                  ? t('scoring.toolbar.tooMany', { limit: dimensionLimit })
                  : dimensionsLoading
                    ? t('scoring.toolbar.reading')
                    : t('scoring.toolbar.perDataset', { count: shown })}
              </span>
            </span>
          </label>
        </div>
      </Popover>

      <Popover label={t('scoring.toolbar.columns')}>
        <div className="lq-pop__group">
          <p className="lq-pop__head">{t('scoring.toolbar.columnsHead')}</p>
          <p className="lq-pop__note">{t('scoring.toolbar.columnsNote')}</p>
          {OPTIONAL_COLUMNS.map((column) => (
            <label key={column} className="lq-pop__check">
              <input
                type="checkbox"
                checked={columns.has(column)}
                onChange={() => {
                  onToggleColumn(column);
                }}
              />
              <span>{t(`scoring.column.${column}`)}</span>
            </label>
          ))}
        </div>
      </Popover>

      <Popover label={t('scoring.toolbar.savedViews')}>
        <div className="lq-pop__group">
          <p className="lq-pop__head">{t('scoring.toolbar.savedHead')}</p>
          <p className="lq-pop__note">{t('scoring.toolbar.savedNote')}</p>
          {views
            .filter((view) => !view.builtIn)
            .map((view) => (
              <div key={view.id} className="lq-pop__row">
                <span>{view.label ?? view.id}</span>
                <button
                  type="button"
                  className="lq-linkish"
                  onClick={() => {
                    onDeleteView(view.id);
                  }}
                >
                  {t('scoring.toolbar.deleteView')}
                </button>
              </div>
            ))}
          {views.every((view) => view.builtIn) && (
            <p className="lq-pop__note">{t('scoring.toolbar.noSaved')}</p>
          )}
          {saving ? (
            <form
              className="lq-pop__row"
              onSubmit={(event) => {
                event.preventDefault();
                const label = draftLabel.trim();
                if (label === '') return;
                onSaveView(label);
                setDraftLabel('');
                setSaving(false);
              }}
            >
              <label className="lq-visually-hidden" htmlFor="scores-view-name">
                {t('scoring.toolbar.viewName')}
              </label>
              <input
                id="scores-view-name"
                className="lq-input"
                value={draftLabel}
                placeholder={t('scoring.toolbar.viewName')}
                onChange={(event) => {
                  setDraftLabel(event.target.value);
                }}
              />
              <button type="submit" className="lq-btn lq-btn--primary lq-btn--compact">
                {t('scoring.toolbar.save')}
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="lq-btn lq-btn--secondary lq-btn--compact"
              disabled={!filtered}
              title={filtered ? undefined : t('scoring.toolbar.nothingToSave')}
              onClick={() => {
                setSaving(true);
              }}
            >
              {t('scoring.toolbar.saveCurrent')}
            </button>
          )}
        </div>
      </Popover>
    </div>
  );
}
